import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const pillars = [
  {
    num: "01",
    title: "Understanding the landscape",
    body: "We map the real buying architecture: who holds the budget, what the decision process requires, and what has to be true for a contract to happen.",
  },
  {
    num: "02",
    title: "Positioning credibly",
    body: "We build the evidence, narrative, and commercial structure that cautious buyers need before they commit.",
  },
  {
    num: "03",
    title: "Competing effectively",
    body: "We sequence engagement, sharpen positioning, and maintain momentum through a procurement process designed to move slowly.",
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

export default function Home() {
  const pillarsSection = useInView();
  const ctaSection = useInView();

  useEffect(() => {
    document.title = "RESECOR | National Security & Resilience Market Advisory";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", "Resecor helps proven technology suppliers enter, compete and win in UK national security and resilience markets. Buyer-side insight, supplier-side strategy. From proven capability to contracted outcome, faster.");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `${import.meta.env.VITE_SITE_URL ?? ""}/`);

    const scriptId = "jsonld-home";
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebSite",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#website`,
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/`,
          "name": "Resecor",
          "description": "Specialist advisory firm helping proven technology suppliers enter, compete and win in UK national security and resilience markets.",
          "publisher": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization` }
        },
        {
          "@type": "ProfessionalService",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization`,
          "name": "Resecor",
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/`,
          "email": "enquiries@resecor.com",
          "description": "Resecor is a specialist advisory firm for technology companies pursuing contracts in UK national security, defence, space, cyber, and critical infrastructure markets.",
          "areaServed": "GB",
          "serviceType": ["Market Entry Advisory", "Defence Procurement Strategy", "National Security Consulting", "Resilience Market Advisory"]
        },
        {
          "@type": "WebPage",
          "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#webpage`,
          "url": `${import.meta.env.VITE_SITE_URL ?? ""}/`,
          "name": "RESECOR | National Security & Resilience Market Advisory",
          "description": "Resecor helps proven technology suppliers enter, compete and win in UK national security and resilience markets.",
          "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#website` },
          "about": { "@id": `${import.meta.env.VITE_SITE_URL ?? ""}/#organization` }
        }
      ]
    });

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-navy font-body">
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/6a54d979427521597e3cc7ddde4d5b0f.jpg"
            alt="RESECOR hero background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/85 via-navy/75 to-navy/90" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/60 via-transparent to-navy/40" />
        </div>

        {/* Decorative copper line */}
        <div className="absolute left-0 top-0 h-full w-0.5 bg-gradient-to-b from-transparent via-copper/40 to-transparent z-10" />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-24">
          <div
            className="opacity-0 animate-fade-in"
            style={{ animationDelay: "0.1s", animationFillMode: "forwards" }}
          >
            <span className="inline-block font-heading text-xs tracking-[0.25em] uppercase text-copper mb-6 font-medium">
              Resecor Advisory
            </span>
          </div>

          <h1
            className="font-heading font-bold text-4xl md:text-6xl lg:text-7xl text-white leading-[1.1] tracking-tight max-w-3xl opacity-0 animate-fade-up"
            style={{ animationDelay: "0.2s", animationFillMode: "forwards" }}
          >
            From Proven<br />
            <span className="text-copper">to Contracted.</span>
          </h1>

          <p
            className="mt-8 font-body text-base md:text-lg text-mid-grey leading-relaxed max-w-2xl opacity-0 animate-fade-up"
            style={{ animationDelay: "0.4s", animationFillMode: "forwards" }}
          >
            We help proven technology suppliers enter, compete and win in national security and resilience markets.
          </p>
          <p
            className="mt-3 font-body text-base md:text-lg text-mid-grey/80 leading-relaxed max-w-xl opacity-0 animate-fade-up"
            style={{ animationDelay: "0.5s", animationFillMode: "forwards" }}
          >
            We bring buyer-side insight to supplier-side strategy, so that credible companies can navigate procurement faster and with less wasted resource.
          </p>

          <div
            className="mt-12 flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-up"
            style={{ animationDelay: "0.65s", animationFillMode: "forwards" }}
          >
            <Link
              to="/offers"
              className="px-7 py-3.5 bg-copper text-white font-body font-medium text-sm tracking-wide hover:bg-copper-light transition-colors duration-200 cursor-pointer whitespace-nowrap rounded-sm"
            >
              Explore Offers
            </Link>
            <Link
              to="/contact"
              className="px-7 py-3.5 border border-white/30 text-white font-body font-medium text-sm tracking-wide hover:border-white/60 hover:bg-white/5 transition-all duration-200 cursor-pointer whitespace-nowrap rounded-sm"
            >
              Start a Conversation
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 opacity-60">
          <div className="w-px h-8 bg-gradient-to-b from-transparent to-mid-grey animate-pulse" />
        </div>
      </section>

      {/* ── THREE PILLARS ── */}
      <section className="bg-slate py-20 md:py-28" ref={pillarsSection.ref}>
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div
            className={`transition-all duration-700 ${pillarsSection.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          >
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-white leading-tight max-w-lg">
              We work on three things.
            </h2>
            <p className="mt-4 font-body text-base text-mid-grey max-w-2xl leading-relaxed">
              Each is necessary. Together, they move a credible supplier from market interest to contracted outcome.
            </p>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 rounded-sm overflow-hidden">
            {pillars.map((p, i) => (
              <div
                key={p.num}
                className={`bg-slate p-8 md:p-10 flex flex-col gap-4 group transition-all duration-700 hover:bg-charcoal ${
                  pillarsSection.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${i * 120 + 200}ms` }}
              >
                <span className="font-heading text-4xl font-bold text-copper/30 group-hover:text-copper/50 transition-colors duration-300 leading-none">
                  {p.num}
                </span>
                <h3 className="font-heading font-semibold text-lg text-white leading-snug">
                  {p.title}
                </h3>
                <div className="w-8 h-px bg-copper/40 group-hover:w-16 group-hover:bg-copper transition-all duration-300" />
                <p className="font-body text-sm text-mid-grey leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STAT / TRUST BAR ── */}
      <section className="bg-navy border-y border-white/5 py-10 md:py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {[
              { label: "Founded by practitioners", sub: "Operators, advisors, and strategists" },
              { label: "Buyer-side insight", sub: "In uniform, in government, in commerce" },
              { label: "Built for pace", sub: "Weeks not months, decisions not reports" },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center gap-1.5">
                <span className="font-heading font-semibold text-base text-white">{stat.label}</span>
                <span className="font-body text-xs text-mid-grey/70">{stat.sub}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA SECTION ── */}
      <section className="relative bg-slate py-24 md:py-32 overflow-hidden" ref={ctaSection.ref}>
        <div className="absolute inset-0 z-0">
          <img
            src="https://readdy.ai/api/search-image?query=Minimalist%20abstract%20dark%20background%20with%20subtle%20geometric%20grid%20lines%20and%20faint%20copper%20accent%20tones%2C%20deep%20navy%20and%20charcoal%20gradient%2C%20professional%20strategic%20context%2C%20no%20people%2C%20no%20text%2C%20clean%20and%20sophisticated%2C%20dark%20professional%20atmosphere%20suitable%20for%20overlay%20text&width=1440&height=600&seq=cta-resecor-02&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="absolute left-0 top-0 w-1 h-full bg-gradient-to-b from-transparent via-copper/30 to-transparent z-10" />
        <div
          className={`relative z-10 max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 transition-all duration-700 ${
            ctaSection.inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <div className="max-w-xl">
            <h2 className="font-heading font-bold text-2xl md:text-4xl text-white leading-tight">
              Ready to move from market interest<br className="hidden md:block" /> to contracted outcome?
            </h2>
            <p className="mt-4 font-body text-base text-mid-grey leading-relaxed">
              Tell us what you are working on. We will tell you whether we can help.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <Link
              to="/offers"
              className="px-7 py-3.5 border border-copper text-copper hover:bg-copper hover:text-white transition-all duration-200 font-body font-medium text-sm tracking-wide cursor-pointer whitespace-nowrap rounded-sm"
            >
              View Offers
            </Link>
            <Link
              to="/contact"
              className="px-7 py-3.5 bg-copper text-white hover:bg-copper-light transition-colors duration-200 font-body font-medium text-sm tracking-wide cursor-pointer whitespace-nowrap rounded-sm"
            >
              Start a Conversation
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
